import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { createEvent } from '../services/eventService';
import { toast } from 'react-toastify';
import styles from './EventForm.module.css';
import { locationApi, Location, LocationCreate } from '../services/locationApi';

interface EventFormData {
  title: string;
  sub_title: string;
  notification_enabled: boolean;
  Branding: string;
  register_start_date: string;
  register_end_date: string;
  vote_end_date: string;
  event_date: string;
  min_select_users: number;
  max_select_users: number;
  round: number;
  location_name: string;
  country: string;
  city: string;
  postal_code: string;
  street: string;
  house_number: string;
}

const initialFormData: EventFormData = {
  title: '',
  sub_title: '',
  notification_enabled: false,
  Branding: 'MatchMaker',
  register_start_date: '',
  register_end_date: '',
  vote_end_date: '',
  event_date: '',
  min_select_users: 1,
  max_select_users: 10,
  round: 3,
  location_name: '',
  country: '',
  city: '',
  postal_code: '',
  street: '',
  house_number: ''
};

interface EventFormProps {
  onEventCreated: () => void;
}

export const EventForm: React.FC<EventFormProps> = ({ onEventCreated }) => {
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [minDateLimits, setMinDateLimits] = useState({
    register_start_date: '',
    register_end_date: '',
    vote_end_date: '',
    event_date: ''
  });

  // Location state
  const [savedLocations, setSavedLocations] = useState<Location[]>([]);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>('');

  // Initialize date constraints on component mount
  useEffect(() => {
    const now = new Date();
    const currentDateTimeString = now.toISOString().slice(0, 16);
    
    setMinDateLimits({
      register_start_date: currentDateTimeString,
      register_end_date: currentDateTimeString,
      vote_end_date: currentDateTimeString,
      event_date: currentDateTimeString
    });

    // Load saved locations
    loadSavedLocations();
  }, []);

  // Load saved locations from API
  const loadSavedLocations = async () => {
    try {
      setLoadingLocations(true);
      const locations = await locationApi.getAllLocations(true); // Include inactive locations
      setSavedLocations(locations);
    } catch (error) {
      console.error('Error loading saved locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Save current location
  const saveCurrentLocation = async () => {
    if (!formData.location_name || !formData.street || !formData.house_number || 
        !formData.postal_code || !formData.city || !formData.country) {
      toast.warning('Please fill in all location fields before saving');
      return;
    }

    try {
      const locationData: LocationCreate = {
        location_name: formData.location_name,
        street: formData.street,
        house_number: formData.house_number,
        postal_code: formData.postal_code,
        city: formData.city,
        country: formData.country
      };

      await locationApi.createLocation(locationData);
      toast.success('Location saved successfully!');
      loadSavedLocations(); // Reload locations
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Failed to save location');
    }
  };

  // Load a saved location into form
  const loadLocation = (location: Location) => {
    setFormData(prev => ({
      ...prev,
      location_name: location.location_name,
      street: location.street,
      house_number: location.house_number,
      postal_code: location.postal_code,
      city: location.city,
      country: location.country
    }));
    setShowLocationModal(false);
    setLocationSearchTerm(''); // Clear search when closing
    toast.success('Location loaded successfully!');
  };

  // Delete a saved location
  const deleteLocation = async (locationId: string) => {
    try {
      await locationApi.deleteLocation(locationId);
      toast.success('Location deleted successfully!');
      loadSavedLocations(); // Reload locations
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Failed to delete location');
    }
  };

  // Activate a deactivated location
  const activateLocation = async (locationId: string) => {
    try {
      await locationApi.activateLocation(locationId);
      toast.success('Location activated successfully!');
      loadSavedLocations(); // Reload locations
    } catch (error) {
      console.error('Error activating location:', error);
      toast.error('Failed to activate location');
    }
  };

  // Filter locations based on search term
  const filteredLocations = savedLocations.filter(location => {
    if (!locationSearchTerm) return true;
    
    const search = locationSearchTerm.toLowerCase();
    return (
      location.location_name.toLowerCase().includes(search) ||
      location.street.toLowerCase().includes(search) ||
      location.house_number.toLowerCase().includes(search) ||
      location.postal_code.toLowerCase().includes(search) ||
      location.city.toLowerCase().includes(search) ||
      location.country.toLowerCase().includes(search)
    );
  });

  // Helper function to add days to a date
  const addDays = (dateStr: string, days: number): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 16);
  };

  // Handle input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });

    // Update date constraints when dates change
    if (id === 'register_start_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        register_end_date: addDays(value, 1),
        vote_end_date: addDays(value, 2),
        event_date: addDays(value, 3)
      }));
    } else if (id === 'register_end_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        vote_end_date: addDays(value, 1),
        event_date: addDays(value, 2)
      }));
    } else if (id === 'vote_end_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        event_date: addDays(value, 1)
      }));
    } else if (id === 'min_select_users') {
      // Ensure max_select_users is always greater than min_select_users
      const minVal = parseInt(value, 10);
      if (!isNaN(minVal) && minVal >= 2) {
        const maxVal = parseInt(formData.max_select_users.toString(), 10);
        if (maxVal <= minVal) {
          setFormData(prev => ({
            ...prev,
            max_select_users: minVal + 1
          }));
        }
      }
    } else if (id === 'max_select_users') {
      // Ensure max_select_users is always greater than min_select_users
      const maxVal = parseInt(value, 10);
      const minVal = parseInt(formData.min_select_users.toString(), 10);
      if (!isNaN(maxVal) && !isNaN(minVal) && maxVal <= minVal) {
        setFormData(prev => ({
          ...prev,
          max_select_users: minVal + 1
        }));
      }
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setFormData({
      ...formData,
      [id]: checked
    });
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Convert string dates to ISO format
      const formattedData = {
        ...formData,
        register_start_date: new Date(formData.register_start_date).toISOString(),
        register_end_date: new Date(formData.register_end_date).toISOString(),
        vote_end_date: new Date(formData.vote_end_date).toISOString(),
        event_date: new Date(formData.event_date).toISOString(),
        min_select_users: Number(formData.min_select_users),
        max_select_users: Number(formData.max_select_users),
        round: Number(formData.round)
      };

      const response = await createEvent(formattedData);
      
      // Consider it successful if we get a response (even if it's empty)
      // The API returns 200 OK which means it was successful
      setFormData(initialFormData);
      onEventCreated();
      
      // Show success toast
      toast.success('Event was successfully created!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      // Close modal - improved modal closing logic
      closeModal();
    } catch (err) {
      console.error('Error creating event:', err);
      setError('An error occurred while creating the event.');
      toast.error('An error occurred while creating the event.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to close the modal
  const closeModal = () => {
    const modal = document.getElementById('eventModal');
    if (modal) {
      // Try to use Bootstrap 5 API if available
      if (window.bootstrap && window.bootstrap.Modal) {
        const modalInstance = window.bootstrap.Modal.getInstance(modal);
        if (modalInstance) {
          modalInstance.hide();
        }
      } else {
        // Fallback method if Bootstrap JS is not properly loaded
        const closeButton = modal.querySelector('.btn-close');
        if (closeButton) {
          (closeButton as HTMLElement).click();
        }
      }
    }
  };

  return (
    <form id="eventForm" onSubmit={handleSubmit}>
      {error && (
        <div className={`${styles.alert} ${styles.alertDanger}`} role="alert">
          {error}
        </div>
      )}
      
      {/* Event Details Section */}
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            <i className="bi bi-calendar-event-fill"></i>
          </span>
          <div>
            <h6>Event Details</h6>
            <p>Set the event title and branding.</p>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="title">
            <i className="bi bi-card-heading"></i>
            Title
          </label>
          <input 
            type="text" 
            className={styles.formControl}
            id="title" 
            value={formData.title}
            onChange={handleInputChange}
            required 
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="sub_title">
            <i className="bi bi-text-paragraph"></i>
            Subtitle
          </label>
          <textarea 
            className={styles.formControl}
            id="sub_title" 
            rows={2} 
            value={formData.sub_title}
            onChange={handleInputChange}
            required
          ></textarea>
        </div>

        <div className={styles.formRow}>
          <label htmlFor="notification_enabled" className={styles.checkboxWrapper}>
            <div className={styles.checkboxLabel}>
              <i className="bi bi-bell-fill"></i>
              <span>Notification enabled</span>
            </div>
            <input 
              type="checkbox" 
              id="notification_enabled" 
              checked={formData.notification_enabled}
              onChange={handleCheckboxChange}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
            />
            <span style={{
              position: 'relative',
              width: '40px',
              height: '22px',
              background: formData.notification_enabled ? 'linear-gradient(135deg, #5a7dff 0%, #a8c0ff 100%)' : '#cbd5e1',
              borderRadius: '34px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              <span style={{
                content: '',
                position: 'absolute',
                height: '16px',
                width: '16px',
                left: '3px',
                top: '3px',
                background: 'white',
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transform: formData.notification_enabled ? 'translateX(18px)' : 'translateX(0)'
              }}></span>
            </span>
          </label>

          <div className={styles.formGroup}>
            <label htmlFor="Branding">
              <i className="bi bi-palette-fill"></i>
              Branding
            </label>
            <select 
              className={styles.formSelect}
              id="Branding"
              value={formData.Branding}
              onChange={handleInputChange}
            >
              <option value="MatchMaker">MatchMaker</option>
              <option value="Managing Partners' Speed Dating">Managing Partners' Speed Dating</option>
            </select>
          </div>
        </div>
      </div>

      {/* Event Dates Section */}
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            <i className="bi bi-clock-history"></i>
          </span>
          <div>
            <h6>Event Dates</h6>
            <p>Define all important dates and deadlines.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.25rem', marginBottom: '0.25rem' }}>
          <div className={styles.formGroup}>
            <label htmlFor="register_start_date">
              <i className="bi bi-play-circle-fill"></i>
              Reg. Start
            </label>
            <input 
              type="datetime-local" 
              className={styles.formControl}
              id="register_start_date"
              min={minDateLimits.register_start_date}
              value={formData.register_start_date}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="register_end_date">
              <i className="bi bi-stop-circle-fill"></i>
              Reg. End
            </label>
            <input 
              type="datetime-local" 
              className={styles.formControl}
              id="register_end_date"
              min={minDateLimits.register_end_date}
              value={formData.register_end_date}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="vote_end_date">
              <i className="bi bi-hand-thumbs-up-fill"></i>
              Vote End
            </label>
            <input 
              type="datetime-local" 
              className={styles.formControl}
              id="vote_end_date"
              min={minDateLimits.vote_end_date}
              value={formData.vote_end_date}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="event_date">
              <i className="bi bi-calendar-check-fill"></i>
              Event Date
            </label>
            <input 
              type="datetime-local" 
              className={styles.formControl}
              id="event_date"
              min={minDateLimits.event_date}
              value={formData.event_date}
              onChange={handleInputChange}
              required 
            />
          </div>
        </div>
      </div>

      {/* Participant Settings Section */}
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            <i className="bi bi-people-fill"></i>
          </span>
          <div>
            <h6>Participant Settings</h6>
            <p>Define min/max participants and rounds.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.25rem', marginBottom: '0.25rem' }}>
          <div className={styles.formGroup}>
            <label htmlFor="min_select_users">
              <i className="bi bi-person-fill-down"></i>
              Min. Participants
            </label>
            <input 
              type="number" 
              className={styles.formControl}
              id="min_select_users"
              min="2"
              value={formData.min_select_users}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="max_select_users">
              <i className="bi bi-person-fill-up"></i>
              Max. Participants
            </label>
            <input 
              type="number" 
              className={styles.formControl}
              id="max_select_users"
              min={parseInt(formData.min_select_users.toString(), 10) + 1}
              value={formData.max_select_users}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="round">
              <i className="bi bi-arrow-repeat"></i>
              Rounds
            </label>
            <input 
              type="number" 
              className={styles.formControl}
              id="round"
              value={formData.round}
              onChange={handleInputChange}
              required 
            />
          </div>
        </div>
      </div>

      {/* Location Details Section */}
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            <i className="bi bi-geo-alt-fill"></i>
          </span>
          <div style={{ flex: 1 }}>
            <h6>Location Details</h6>
            <p>Specify where the event will take place.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setShowLocationModal(true)}
              className="btn btn-sm"
              style={{
                background: 'linear-gradient(135deg, #5a7dff 0%, #a8c0ff 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                padding: '0.4rem 0.8rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <i className="bi bi-folder-fill"></i>
              Load
              {savedLocations.filter(loc => loc.is_active).length > 0 && (
                <span style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  padding: '0.1rem 0.4rem',
                  fontSize: '0.7rem',
                  fontWeight: '700'
                }}>
                  {savedLocations.filter(loc => loc.is_active).length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={saveCurrentLocation}
              className="btn btn-sm"
              style={{
                background: 'linear-gradient(135deg, #d22332 0%, #a01c28 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                padding: '0.4rem 0.8rem',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}
            >
              <i className="bi bi-save-fill me-1"></i>
              Save
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="location_name">
            <i className="bi bi-building"></i>
            Location Name
          </label>
          <input 
            type="text" 
            className={styles.formControl}
            id="location_name"
            value={formData.location_name}
            onChange={handleInputChange}
            required 
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.25rem', marginBottom: '0.25rem' }}>
          <div className={styles.formGroup}>
            <label htmlFor="street">
              <i className="bi bi-signpost-fill"></i>
              Street
            </label>
            <input 
              type="text" 
              className={styles.formControl}
              id="street"
              value={formData.street}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="house_number">
              <i className="bi bi-house-door-fill"></i>
              House Number
            </label>
            <input 
              type="text" 
              className={styles.formControl}
              id="house_number"
              value={formData.house_number}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="postal_code">
              <i className="bi bi-mailbox"></i>
              Postal Code
            </label>
            <input 
              type="text" 
              className={styles.formControl}
              id="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              required 
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', marginBottom: '0.25rem' }}>
          <div className={styles.formGroup}>
            <label htmlFor="city">
              <i className="bi bi-pin-map-fill"></i>
              City
            </label>
            <input 
              type="text" 
              className={styles.formControl}
              id="city"
              value={formData.city}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="country">
              <i className="bi bi-flag-fill"></i>
              Country
            </label>
            <input 
              type="text" 
              className={styles.formControl}
              id="country"
              value={formData.country}
              onChange={handleInputChange}
              required 
            />
          </div>
        </div>
      </div>

      {/* Saved Locations Modal */}
      {showLocationModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowLocationModal(false)}>
          <div className="modal-dialog modal-dialog-centered modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', boxShadow: '0 24px 48px rgba(18, 18, 18, 0.18)' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #eceff4', padding: '1.25rem 1.5rem', background: '#ffffff' }}>
                <div>
                  <h5 className="modal-title mb-1" style={{ fontWeight: '700', color: '#1a1a1a', fontSize: '1.1rem' }}>
                    <i className="bi bi-folder-fill me-2" style={{ color: '#5a7dff' }}></i>
                    Saved Locations
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    Select a location to load into the event form
                  </p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowLocationModal(false)}></button>
              </div>
              <div className="modal-body" style={{ padding: '0', maxHeight: '70vh', minHeight: '500px', overflowY: 'auto', background: '#f9fafb' }}>
                {loadingLocations ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3" style={{ color: '#6b7280' }}>Loading saved locations...</div>
                  </div>
                ) : (
                  <>
                    {/* Search Bar */}
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                      <div style={{ position: 'relative' }}>
                        <div style={{ 
                          position: 'absolute', 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          color: '#6b7280',
                          pointerEvents: 'none'
                        }}>
                          <i className="bi bi-search"></i>
                        </div>
                        <input 
                          type="text"
                          className="form-control"
                          placeholder="Search by name, address, city, or country..."
                          value={locationSearchTerm}
                          onChange={(e) => setLocationSearchTerm(e.target.value)}
                          style={{
                            paddingLeft: '2.5rem',
                            paddingRight: locationSearchTerm ? '2.5rem' : '1rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            fontSize: '0.9rem'
                          }}
                        />
                        {locationSearchTerm && (
                          <button 
                            type="button"
                            onClick={() => setLocationSearchTerm('')}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: '#6b7280',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Clear search"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </div>
                      {locationSearchTerm && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Found {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    {savedLocations.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="bi bi-inbox" style={{ fontSize: '4rem', color: '#cbd5e1', display: 'block', marginBottom: '1.5rem' }}></i>
                        <h5 style={{ color: '#6b7280', marginBottom: '0.75rem' }}>No saved locations yet</h5>
                        <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                          Save your first location to reuse it in future events
                        </p>
                      </div>
                    ) : (
                      <div style={{ background: '#ffffff' }}>
                        <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                          <thead style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ padding: '1rem 1.5rem', fontWeight: '600', fontSize: '0.85rem', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                                <i className="bi bi-building me-2"></i>
                                Location Name
                              </th>
                              <th style={{ padding: '1rem 1.5rem', fontWeight: '600', fontSize: '0.85rem', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                                <i className="bi bi-signpost-fill me-2"></i>
                                Address
                              </th>
                              <th style={{ padding: '1rem 1.5rem', fontWeight: '600', fontSize: '0.85rem', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                                <i className="bi bi-pin-map-fill me-2"></i>
                                City & Country
                              </th>
                              <th style={{ padding: '1rem 1.5rem', fontWeight: '600', fontSize: '0.85rem', color: '#374151', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>
                                <i className="bi bi-tools me-2"></i>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLocations.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center py-5">
                                  <i className="bi bi-search" style={{ fontSize: '3rem', color: '#cbd5e1', display: 'block', marginBottom: '1rem' }}></i>
                                  <h6 style={{ color: '#6b7280', marginBottom: '0.5rem' }}>No locations found</h6>
                                  <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                    Try adjusting your search criteria
                                  </p>
                                  <button 
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setLocationSearchTerm('')}
                                  >
                                    <i className="bi bi-x-circle me-2"></i>
                                    Clear Search
                                  </button>
                                </td>
                              </tr>
                            ) : (
                              filteredLocations.map((location, index) => (
                          <tr 
                            key={location.Location_ID} 
                            style={{ 
                              background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                              borderBottom: '1px solid #e5e7eb',
                              transition: 'background-color 0.2s ease',
                              opacity: location.is_active ? 1 : 0.6
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                          >
                            <td style={{ padding: '1rem 1.5rem', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ 
                                  width: '36px', 
                                  height: '36px', 
                                  borderRadius: '10px', 
                                  background: location.is_active 
                                    ? 'linear-gradient(135deg, #5a7dff 0%, #a8c0ff 100%)'
                                    : 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '0.75rem',
                                  flexShrink: 0
                                }}>
                                  <i className="bi bi-building" style={{ color: 'white', fontSize: '1rem' }}></i>
                                </div>
                                <div>
                                  <strong style={{ fontSize: '0.95rem', color: location.is_active ? '#1a1a1a' : '#6b7280' }}>
                                    {location.location_name}
                                  </strong>
                                  {!location.is_active && (
                                    <div style={{ marginTop: '0.25rem' }}>
                                      <span style={{
                                        fontSize: '0.7rem',
                                        background: '#fef3c7',
                                        color: '#92400e',
                                        padding: '0.15rem 0.5rem',
                                        borderRadius: '4px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}>
                                        <i className="bi bi-exclamation-circle-fill me-1"></i>
                                        Deactivated
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '1rem 1.5rem', verticalAlign: 'middle' }}>
                              <div style={{ fontSize: '0.85rem', color: location.is_active ? '#6b7280' : '#9ca3af' }}>
                                {location.street} {location.house_number}
                                <br />
                                <small style={{ color: '#9ca3af' }}>{location.postal_code}</small>
                              </div>
                            </td>
                            <td style={{ padding: '1rem 1.5rem', verticalAlign: 'middle' }}>
                              <div style={{ fontSize: '0.85rem', color: location.is_active ? '#6b7280' : '#9ca3af' }}>
                                <i className="bi bi-geo-alt-fill me-1" style={{ color: location.is_active ? '#5a7dff' : '#9ca3af' }}></i>
                                {location.city}
                                <br />
                                <small style={{ color: '#9ca3af' }}>
                                  <i className="bi bi-flag-fill me-1" style={{ fontSize: '0.75rem' }}></i>
                                  {location.country}
                                </small>
                              </div>
                            </td>
                            <td style={{ padding: '1rem 1.5rem', verticalAlign: 'middle', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                {location.is_active ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => loadLocation(location)}
                                      className="btn btn-sm"
                                      title="Load this location"
                                      style={{
                                        background: 'linear-gradient(135deg, #5a7dff 0%, #a8c0ff 100%)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: 'white',
                                        padding: '0.4rem 0.8rem',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 8px rgba(90, 125, 255, 0.3)',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(90, 125, 255, 0.4)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(90, 125, 255, 0.3)';
                                      }}
                                    >
                                      <i className="bi bi-check-circle-fill me-1"></i>
                                      Load
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => deleteLocation(location.Location_ID)}
                                      className="btn btn-sm"
                                      title="Delete this location"
                                      style={{
                                        background: '#ef4444',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: 'white',
                                        padding: '0.4rem 0.8rem',
                                        fontSize: '0.85rem',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#dc2626';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#ef4444';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => activateLocation(location.Location_ID)}
                                    className="btn btn-sm"
                                    title="Activate this location"
                                    style={{
                                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                                      border: 'none',
                                      borderRadius: '8px',
                                      color: 'white',
                                      padding: '0.4rem 0.8rem',
                                      fontSize: '0.85rem',
                                      fontWeight: '600',
                                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                    }}
                                  >
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Activate
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="modal-footer" style={{ background: '#f9fafb', borderTop: '1px solid #eceff4', padding: '0.5rem 0', marginTop: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={submitting}
          style={{
            background: 'linear-gradient(135deg, #5a7dff 0%, #a8c0ff 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '0.7rem 1.4rem',
            fontWeight: '600',
            fontSize: '0.9rem',
            boxShadow: '0 16px 28px rgba(90, 125, 255, 0.4)',
            transition: 'all 0.2s ease'
          }}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle-fill me-2"></i>
              Create Event
            </>
          )}
        </button>
      </div>
    </form>
  );
};
